import styled from '@emotion/styled';

import { screen } from '@/components/common/breakpoints';
import { Button } from '@/components/common/Buttons';
import { CaptionHeadingDescription } from '@/components/common/CaptionHeadingDescription';
import { Image } from '@/components/common/image';
import { Heading, HeadingLevel, Paragraph } from '@/components/common/MarkUp';
import { Section } from '@/components/layout/Section';
import { getChildBackgroundColourToken } from '@/lib/colourUtils';
import { blocksToText } from '@/lib/sanityUtils';
import { blockUrlWithAudience } from '@/lib/utils';
import { labelMedium } from '@/styles/typography';

import type {
  SanityAudienceType,
  SanityImageAsset,
  SanityImageCrop,
  SanityImageHotspot,
  SanityKeyed,
  SanityReference,
  SanityThreeCardColumnsType,
} from '@/types/sanity';
import type { BlockWithAudience, TrackingTagType } from '@/types/shared';

interface SingleColumnProps {
  _type: 'column';
  title: string;
  subtitle: string;
  timeToReadMinutes: number;
  audienceRef?: SanityReference<SanityAudienceType>;
  noAudienceToggle?: boolean;
  url: string;
  image: {
    _type: 'image';
    asset: SanityReference<SanityImageAsset>;
    crop?: SanityImageCrop;
    hotspot?: SanityImageHotspot;
    alternateText?: string;
  };
  isDecorativeImage?: boolean;
  trackingTag?: TrackingTagType;
}

export interface ThreeCardColumnsProps
  extends Omit<SanityThreeCardColumnsType, 'columns' | 'audienceRef'>,
    BlockWithAudience {
  columns: Array<
    SanityKeyed<Omit<SingleColumnProps, 'audienceRef'>> & {
      audienceRef?: SanityAudienceType;
    }
  >;
  audienceRef?: SanityAudienceType;
}

/**
- Use an avatar for attributing actions or content to specific users.
- The user's name should always be present when using Avatar – either printed beside the avatar or in a tooltip.

This can be used to link out to the Multiverse blog or any other external pages that are not our own site.
*/

const ThreeCardColumns = ({
  heading,
  columns,
  viewMoreLink,
  trackingTag,
  backgroundColour,
  blockSpacing,
  paddingAdjustment,

  audienceRef,
  noAudienceToggle,
  pageAudience,
  isAudienceSwitcherEnabled,
}: ThreeCardColumnsProps) => (
  <Section
    verticalPadding={paddingAdjustment ?? true}
    spacing={blockSpacing}
    backgroundColour={backgroundColour?.token}
    className="three-card-columns"
  >
    <HeadingLevel>
      <ThreeCardColumnsContainer>
        <CaptionHeadingDescription heading={heading} />
        <Columns>
          {columns.map((column) => (
            <Column
              backgroundColourToken={getChildBackgroundColourToken(
                backgroundColour?.token,
                '--background-subtle',
              )}
              key={column._key}
              className={
                column.trackingTag?.blockName &&
                `tracking-${column.trackingTag.blockName}__${column.trackingTag.blockAction}`
              }
            >
              <HeadingLevel>
                {/* eslint-disable-next-line */}
                <a
                  href={blockUrlWithAudience(
                    column.url,
                    column.audienceRef,
                    pageAudience,
                    column.noAudienceToggle,
                    isAudienceSwitcherEnabled,
                  )}
                  target="_blank"
                >
                  <ImageContainer>
                    {!!column.image && (
                      <Image
                        {...column.image}
                        isDecorativeImage={column.isDecorativeImage}
                      />
                    )}
                  </ImageContainer>
                  <CopyContainer>
                    <Heading size="heading-md" title={column.title}>
                      {column.title}
                    </Heading>
                    <Paragraph size="subheading-sm" title={column.subtitle}>
                      {column.subtitle}
                    </Paragraph>
                    <Paragraph size="subheading-sm">
                      {column.timeToReadMinutes} min read
                    </Paragraph>
                  </CopyContainer>
                </a>
              </HeadingLevel>
            </Column>
          ))}
        </Columns>
        <Button
          variant="flat-light"
          href={blockUrlWithAudience(
            viewMoreLink,
            audienceRef,
            pageAudience,
            noAudienceToggle,
            isAudienceSwitcherEnabled,
          )}
          aria-label={blocksToText(heading)}
          className={
            trackingTag?.blockName &&
            `tracking-${trackingTag.blockName}__${trackingTag.blockAction}`
          }
        >
          View more
        </Button>
      </ThreeCardColumnsContainer>
    </HeadingLevel>
  </Section>
);

const ThreeCardColumnsContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`;

const Columns = styled.div`
  margin: var(--spacing-xx-large) 0 var(--spacing-large);
  gap: var(--spacing-medium);
  display: flex;
  flex-direction: column;

  ${screen.md} {
    flex-direction: row;
    gap: var(--spacing-medium);
  }
`;

const Column = styled.article<{ backgroundColourToken }>`
  display: flex;
  flex-direction: column;
  width: 100%;

  border: 1px solid rgba(0, 0, 0, 0.25);
  border-radius: var(--radius-l);
  overflow: hidden;

  transition: all 0.3s;

  &:hover {
    transform: scale(1.03);
  }

  a {
    background-color: ${({ backgroundColourToken }) =>
      `var(${backgroundColourToken})`};
  }

  ${screen.md} {
    min-height: 450px;
  }

  * {
    text-decoration: none;
    color: var(--text-strong);
  }
`;

const ImageContainer = styled.div`
  display: flex;
  height: 236px;
  flex-direction: column;
  background: transparent;
  overflow: hidden;

  img {
    max-height: unset !important;
    height: auto !important;
  }
`;
const CopyContainer = styled.div`
  min-height: 236px;
  padding: var(--spacing-500);
  display: flex;
  flex-direction: column;
  justify-content: space-between;

  ${Paragraph} {
    &:last-of-type {
      ${labelMedium}
    }
  }

  ${screen.md} {
    h3 {
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: 3;
      -webkit-box-orient: vertical;
    }

    p:first-of-type {
      display: -webkit-box;
      overflow: hidden;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
    }

    padding: var(--spacing-300);
    height: 280px;
  }
`;

export { ThreeCardColumns };
